import { CheckCircle2, ChevronRight, Clock3, RotateCcw, ShieldCheck, XCircle } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { cardFor, masteryLabels, selectQueue, sameAnswerSet } from "../lib/learningEngine";
import { abilityFor, buildOptions, hashSeed, pickDifficulty } from "../lib/difficulty";
import { FlashcardCard } from "./FlashcardCard";
import { NormTextList } from "./NormTextList";
import { ClozeCard } from "./ClozeCard";
import { OrderingCard } from "./OrderingCard";
import { isAdvanceKey, keyToOptionIndex } from "../lib/answerKeys";
import { vibrate } from "../lib/haptics";
import type { SoundService } from "../lib/sound";
import type { Difficulty, Grade, LearningExercise, LearningPathId, LearningState, ModeId } from "../types";

interface PracticePanelProps {
  exercises: LearningExercise[];
  state: LearningState;
  mode: ModeId;
  pathId: LearningPathId | "all";
  onSubmit: (exercise: LearningExercise, selected: number[], elapsedSeconds: number, grade?: Grade, difficulty?: Difficulty) => void;
  onRunComplete?: () => void;
  soundService?: SoundService;
  hapticsEnabled?: boolean;
}

const BURST_PARTICLES = Array.from({ length: 16 }, (_, index) => index);

function modeCopy(mode: ModeId) {
  return {
    daily: {
      title: "Daily Drill",
      subtitle: "Kurze taegliche Uebung, die sich anpasst: wiederholt Faelliges und bringt Neues dazu.",
      limit: 8,
    },
    case: {
      title: "Case Gym",
      subtitle: "Gefuehrte Fall-Subsumtion Schritt fuer Schritt, mit Normtext.",
      limit: 10,
    },
    claim: {
      title: "Claim Lab",
      subtitle: "Anspruchsanalyse: Merkmale, Auslegung, EPUe-nahe Claim-Arbeit.",
      limit: 10,
    },
    exam: {
      title: "Exam Mode",
      subtitle: "Pruefung auf Zeit: gemischte Fragen, keine Hilfen, kein Wiederholungsfilter.",
      limit: 14,
    },
  }[mode];
}

export function PracticePanel({
  exercises,
  state,
  mode,
  pathId,
  onSubmit,
  onRunComplete,
  soundService,
  hapticsEnabled = true,
}: PracticePanelProps) {
  const copy = modeCopy(mode);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selected, setSelected] = useState<number[]>([]);
  const [startedAt, setStartedAt] = useState(Date.now());
  const [result, setResult] = useState<{ correct: boolean; selected: number[] } | null>(null);
  const nextButtonRef = useRef<HTMLButtonElement | null>(null);

  // Session-Playlist: einmal je Modus/Pfad/Bank bauen, NICHT bei jeder Antwort neu sortieren.
  // Sonst aendert das State-Update nach dem Absenden die Reihenfolge, current wechselt und der
  // current.id-Effekt verwirft das gerade gesetzte Feedback (richtig/falsch verschwindet).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const queue = useMemo(() => selectQueue(state, exercises, mode, pathId, copy.limit), [copy.limit, exercises, mode, pathId]);
  const current = queue[currentIndex] ?? queue[0];
  const card = current ? cardFor(state, current.id) : undefined;

  // Variantenfaehige Items (correctTexts + distractorsByDifficulty) werden adaptiv zur
  // Niveau-Stufe assembliert; statische Items nutzen ihre festen options/answers.
  // Bewusst nur an current.id gekoppelt: bleibt waehrend der Frage + des Feedbacks stabil
  // (kein Reshuffle nach dem Absenden), variiert aber bei erneuter Praesentation.
  const display = useMemo(() => {
    if (!current) {
      return { options: [] as string[], answers: [] as number[], difficulty: undefined as Difficulty | undefined };
    }
    const hasVariants = Boolean(current.distractorsByDifficulty && current.correctTexts);
    if (!hasVariants) {
      // Statische MC: Optionen deterministisch mischen, damit die richtige Antwort nicht
      // systematisch auf Position A klebt (Autoren-Bias: ~88 % der Altfragen hatten A korrekt).
      // Seed = id + bisherige Versuche -> stabil waehrend der Frage, variiert ueber Sessions
      // (Anti-Memorierung). Cloze/Ordering/Flashcard sind oben abgezweigt; Cases mischen bereits.
      if (current.options.length < 2) {
        return { options: current.options, answers: current.answers, difficulty: current.difficulty };
      }
      const triesStatic = (card?.correct ?? 0) + (card?.wrong ?? 0);
      const correctTexts = current.answers.map((i) => current.options[i]);
      const distractors = current.options.filter((_, i) => !current.answers.includes(i));
      const built = buildOptions(correctTexts, distractors, hashSeed(current.id, triesStatic));
      return { options: built.options, answers: built.answers, difficulty: current.difficulty };
    }
    const tries = (card?.correct ?? 0) + (card?.wrong ?? 0);
    const difficulty = pickDifficulty(abilityFor(state.abilities, current.skillTags), hashSeed(current.id, tries));
    const built = buildOptions(
      current.correctTexts as string[],
      (current.distractorsByDifficulty as Partial<Record<Difficulty, string[]>>)[difficulty] ?? [],
      hashSeed(current.id, difficulty, tries)
    );
    return { options: built.options, answers: built.answers, difficulty };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id]);

  useEffect(() => {
    setCurrentIndex(0);
    setSelected([]);
    setResult(null);
    setStartedAt(Date.now());
  }, [mode, pathId]);

  useEffect(() => {
    setSelected([]);
    setResult(null);
    setStartedAt(Date.now());
  }, [current?.id]);

  useEffect(() => {
    if (!result) return;
    nextButtonRef.current?.focus();
  }, [result]);

  const isVariant = Boolean(current?.distractorsByDifficulty && current.correctTexts);
  const isMulti = current ? (isVariant ? display.answers.length > 1 : current.kind !== "single") : false;
  const canSubmit = selected.length > 0 || display.answers.length === 0;
  const elapsedSeconds = Math.max(8, Math.round((Date.now() - startedAt) / 1000));

  useEffect(() => {
    function isTypingTarget(target: EventTarget | null) {
      return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement;
    }

    function handleKeydown(event: KeyboardEvent) {
      if (!current) return;
      if (isTypingTarget(event.target) || current.kind === "flashcard" || current.kind === "ordering" || current.kind === "cloze") return;
      if (result) {
        if (isAdvanceKey(event.key)) {
          event.preventDefault();
          next();
        }
        return;
      }

      const optionIndex = keyToOptionIndex(event.key, display.options.length);
      if (optionIndex !== null) {
        event.preventDefault();
        toggleAnswer(optionIndex);
        return;
      }

      if (isMulti && isAdvanceKey(event.key) && canSubmit) {
        event.preventDefault();
        submit();
      }
    }

    window.addEventListener("keydown", handleKeydown);
    return () => window.removeEventListener("keydown", handleKeydown);
  });

  if (!current) {
    return (
      <section className="practice-panel">
        <div className="panel-heading">
          <span className="eyebrow">{copy.title}</span>
          <h2>Keine passenden Aufgaben.</h2>
          <p>Waehle einen breiteren Lernpfad oder importiere neues Material ueber die Import-Konsole.</p>
        </div>
      </section>
    );
  }

  if (current.kind === "flashcard") {
    return (
      <section className="practice-panel">
        <div className="panel-heading practice-heading">
          <div>
            <span className="eyebrow">{copy.title}</span>
            <h2>{copy.subtitle}</h2>
          </div>
          <div className="session-chip">
            <Clock3 size={16} />
            {currentIndex + 1}/{queue.length}
          </div>
        </div>
        <FlashcardCard
          exercise={current}
          key={current.id}
          onGrade={(grade, elapsed) => {
            onSubmit(current, [], elapsed, grade);
            next();
          }}
        />
      </section>
    );
  }

  if (current.kind === "ordering") {
    return (
      <section className="practice-panel">
        <div className="panel-heading practice-heading">
          <div>
            <span className="eyebrow">{copy.title}</span>
            <h2>{copy.subtitle}</h2>
          </div>
          <div className="session-chip">
            <Clock3 size={16} />
            {currentIndex + 1}/{queue.length}
          </div>
        </div>
        <OrderingCard
          exercise={current}
          key={current.id}
          onComplete={(correct) => {
            onSubmit(current, [], Math.max(8, Math.round((Date.now() - startedAt) / 1000)), correct ? "good" : "again");
            setResult({ correct, selected: [] });
          }}
        />
        {result ? (
          <button className="primary-action" onClick={next} type="button">
            Naechste Aufgabe
            <ChevronRight size={18} />
          </button>
        ) : null}
      </section>
    );
  }

  if (current.kind === "cloze") {
    return (
      <section className="practice-panel">
        <div className="panel-heading practice-heading">
          <div>
            <span className="eyebrow">{copy.title}</span>
            <h2>{copy.subtitle}</h2>
          </div>
          <div className="session-chip">
            <Clock3 size={16} />
            {currentIndex + 1}/{queue.length}
          </div>
        </div>
        <ClozeCard
          exercise={current}
          key={current.id}
          onComplete={(correct) => {
            onSubmit(current, correct ? current.answers : [], Math.max(8, Math.round((Date.now() - startedAt) / 1000)), correct ? "good" : "again");
            setResult({ correct, selected: [] });
          }}
        />
        {result ? (
          <button className="primary-action" onClick={next} type="button">
            Naechste Aufgabe
            <ChevronRight size={18} />
          </button>
        ) : null}
      </section>
    );
  }

  function toggleAnswer(index: number) {
    if (result) return;
    soundService?.playCue("commit");
    vibrate(8, hapticsEnabled);
    if (!isMulti) {
      submit([index]);
      return;
    }
    setSelected((value) => (value.includes(index) ? value.filter((item) => item !== index) : [...value, index]));
  }

  function submit(selection = selected) {
    if (selection.length === 0 && display.answers.length > 0) return;
    const normalized = [...selection].sort((a, b) => a - b);
    const correct = sameAnswerSet(normalized, display.answers);
    setResult({ correct, selected: normalized });
    soundService?.playCue(correct ? "correct" : "wrong");
    onSubmit(current, normalized, elapsedSeconds, correct ? "good" : "again", display.difficulty);
  }

  function next() {
    setCurrentIndex((index) => {
      if (index + 1 >= queue.length) {
        onRunComplete?.();
        return 0;
      }
      return index + 1;
    });
  }

  return (
    <section className="practice-panel">
      <div className="panel-heading practice-heading">
        <div>
          <span className="eyebrow">{copy.title}</span>
          <h2>{copy.subtitle}</h2>
        </div>
        <div className="session-chip">
          <Clock3 size={16} />
          {currentIndex + 1}/{queue.length}
        </div>
      </div>

      <article className={`question-card ${result?.correct ? "is-correct" : result ? "is-wrong" : ""}`}>
        {result?.correct ? <span className="reward-tick" aria-hidden="true" /> : null}
        {result?.correct ? (
          <span className="burst" aria-hidden="true">
            {BURST_PARTICLES.map((particle) => (
              <i key={particle} />
            ))}
          </span>
        ) : null}
        <div className="question-meta">
          <span>Frage {currentIndex + 1}/{queue.length}</span>
          <span>{current.topic}</span>
          {current.sources[0] ? <span>{current.sources[0].label}</span> : null}
        </div>
        <h3>{current.title}</h3>
        <p className="prompt">{current.prompt}</p>

        <div className="option-grid" aria-label="Antwortoptionen">
          {display.options.map((option, index) => {
            const isSelected = selected.includes(index) || result?.selected.includes(index);
            const isCorrect = result && display.answers.includes(index);
            const isWrong = result && result.selected.includes(index) && !display.answers.includes(index);
            return (
              <button
                className={`answer-option ${isSelected ? "is-selected" : ""} ${isCorrect ? "is-answer" : ""} ${
                  isWrong ? "is-miss" : ""
                }`}
                disabled={Boolean(result)}
                key={`${current.id}-${index}-${option}`}
                onClick={() => toggleAnswer(index)}
                type="button"
              >
                <span>{String.fromCharCode(65 + index)}</span>
                {option}
                {result && isCorrect ? <span className="opt-mark ok">✓</span> : null}
                {result && isWrong ? <span className="opt-mark miss">✗</span> : null}
              </button>
            );
          })}
        </div>

        <div className="question-actions">
          {/* "Wechseln" zuerst -> mit justify-content:space-between bleibt es stabil links,
             die Primaer-Aktion (Pruefen/Naechste) immer rechts (kein Springen nach Antwort). */}
          <button className="ghost-action" onClick={next} type="button">
            <RotateCcw size={17} />
            Wechseln
          </button>
          {isMulti && !result ? (
            <button className="primary-action" disabled={!canSubmit && display.answers.length > 0} onClick={() => submit()} type="button">
              <ShieldCheck size={18} />
              Auswahl pruefen
            </button>
          ) : null}
          {result ? (
            <button className="primary-action" onClick={next} ref={nextButtonRef} type="button">
              Naechste Aufgabe
              <ChevronRight size={18} />
            </button>
          ) : null}
        </div>

        {result ? (
          <div className="feedback-panel">
            <strong>
              {result.correct ? <CheckCircle2 size={18} /> : <XCircle size={18} />}
              {result.correct
                ? "Sitzt."
                : `Nicht ganz - richtig ist: ${display.options.filter((_, i) => display.answers.includes(i)).join(" / ")}`}
            </strong>
            <p>{current.explanation}</p>
            <p className="feedback-source">
              Meisterung aktuell: {masteryLabels[Math.round((card?.mastery ?? 1) - 1)]}.
            </p>
            <div className="source-row">
              {current.sources.length ? (
                current.sources.map((source) =>
                  source.url ? (
                    <a href={source.url} key={source.id} rel="noreferrer" target="_blank">
                      {source.label}
                    </a>
                  ) : (
                    <span key={source.id}>{source.label}</span>
                  )
                )
              ) : (
                <span>Quelle fehlt im Manifest</span>
              )}
            </div>
            <NormTextList sources={current.sources} />
          </div>
        ) : null}
      </article>
    </section>
  );
}
